import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, AccountCircle } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import RoleSelector from '../components/RoleSelector';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.multipleRoles) {
        // User has multiple roles - show selector
        setAvailableRoles(result.availableRoles);
        setShowRoleSelector(true);
        setLoading(false);
        return;
      }

      if (result.success) {
        toast.success('Login successful!');
        // Redirect based on role
        if (result.user.role === 'admin') {
          navigate('/admin');
        } else if (result.user.role === 'student') {
          navigate('/student/dashboard');
        } else if (result.user.role === 'coordinator') {
          navigate('/coordinator/dashboard');
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = async (selectedRole) => {
    setShowRoleSelector(false);
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password, selectedRole);

      if (result.success) {
        toast.success(`Logged in as ${selectedRole}!`);
        // Redirect based on selected role
        if (selectedRole === 'admin') {
          navigate('/admin');
        } else if (selectedRole === 'student') {
          navigate('/student/dashboard');
        } else if (selectedRole === 'coordinator') {
          navigate('/coordinator/dashboard');
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #FAF7F2 0%, #E8DCC8 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("/pattern.svg")',
          opacity: 0.05,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(139, 69, 19, 0.12)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/logo.jpeg"
              alt="Chinmudra Academy"
              sx={{
                width: 'auto',
                height: 100,
                maxWidth: '80%',
                mb: 2,
                boxShadow: '0 4px 20px rgba(212, 175, 55, 0.3)',
                borderRadius: 2,
                border: '3px solid',
                borderColor: 'primary.main',
              }}
            />
            <Typography variant="h4" color="primary" fontWeight={700} gutterBottom>
              Chinmudra Academy
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                mb: 2,
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Link
                href="#"
                variant="body2"
                color="primary"
                sx={{ textDecoration: 'none', fontWeight: 500 }}
              >
                Forgot password?
              </Link>
            </Box>

            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                New student or parent?
              </Typography>
              <Link
                href="/register"
                variant="body2"
                color="primary"
                sx={{ textDecoration: 'none', fontWeight: 600 }}
              >
                Register Here
              </Link>
            </Box>
          </form>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            © 2026 Chinmudra Academy. All rights reserved.
          </Typography>
        </Box>
      </Container>
      
      {/* Role Selector Dialog */}
      <RoleSelector
        open={showRoleSelector}
        email={email}
        roles={availableRoles}
        onSelectRole={handleRoleSelect}
        onClose={() => setShowRoleSelector(false)}
      />
    </Box>
  );
};

export default Login;
